import { motion } from "framer-motion";
import { Clock3, MapPin, ShieldCheck, HeartPulse, Sparkles, Waves, Fish, MessagesSquare } from "lucide-react";
import { SectionHeading } from "./SectionHeading";

const CATS = {
  ronda: { label: "Jaga Malam", icon: ShieldCheck, chip: "bg-night text-amber2 border border-nightline" },
  posyandu: { label: "Kesehatan", icon: HeartPulse, chip: "bg-terra/10 text-terra border border-terra/25" },
  pemuda: { label: "Pemuda & Komunitas", icon: Sparkles, chip: "bg-forest/10 text-forest dark:text-gold border border-forest/25" },
  umum: { label: "Gotong Royong", icon: MessagesSquare, chip: "bg-badge text-ink/75 border border-line" },
};

const EVENTS = [
  { day: "Jumat", freq: "Tiap Minggu", title: "Ronda Gabungan & Kopi Dusun", time: "21.00 – Selesai", place: "Pos ronda bergilir antar dusun", cat: "ronda", desc: "Ngopi bareng Pak Kades, sampaikan keluhan langsung tanpa birokrasi." },
  { day: "10", freq: "Tiap Tanggal", title: "Posyandu Balita & Lansia", time: "08.00 – 11.00", place: "Balai Warga Dusun 2", cat: "posyandu", desc: "Penimbangan, imunisasi, dan cek kesehatan gratis bersama bidan desa." },
  { day: "Sabtu", freq: "Minggu Terakhir", title: "Gigs Pemuda & Nobar Wota", time: "19.30 – 22.00", place: "Halaman Balai Desa", cat: "pemuda", desc: "Panggung terbuka untuk band, wota, otaku, dan kreator konten Cianjay." },
  { day: "Minggu", freq: "Minggu Pertama", title: "Kerja Bakti & Cek Irigasi", time: "07.00 – 10.00", place: "Kumpul di gapura masing-masing dusun", cat: "umum", desc: "Bersih saluran air dan inspeksi infrastruktur bareng perangkat desa." },
  { day: "Minggu", freq: "Minggu Ketiga", title: "Mancing Bareng Sedulur Cianjay", time: "15.30 – 18.00", place: "Kolam Warga Dusun 1", cat: "pemuda", icon: Fish, desc: "Kumpul santai komunitas mancing, kadang Pak Kades ikut turun cek spot." },
  { day: "25", freq: "Tiap Tanggal", title: "Musyawarah Dusun (Musdus)", time: "19.30 – 21.30", place: "Aula Balai Desa Cianjay", cat: "umum", icon: Waves, desc: "Forum resmi bulanan: laporan eksekusi aspirasi dan rencana bulan depan." },
];

export const VillageAgenda = () => (
  <section id="agenda" data-testid="agenda-section" className="py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <SectionHeading
          bab="Agenda"
          label="Kalender Desa"
          title="Ritme Kegiatan Cianjay Tiap Bulan"
          sub="Tidak perlu menunggu undangan. Semua kegiatan terbuka — datang, duduk, dan ikut bicara."
          testid="agenda"
        />
        <motion.span
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="rounded-full border border-line bg-surface px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-soft"
          data-testid="agenda-updated-badge"
        >
          Jadwal Rutin · Diperbarui Juli 2026
        </motion.span>
      </div>

      <div className="mt-14 grid md:grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-6">
        {EVENTS.map((e, i) => {
          const cat = CATS[e.cat];
          const Icon = e.icon || cat.icon;
          return (
            <motion.article
              key={i}
              data-testid={`agenda-card-${i}`}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.75, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6 }}
              className="group flex flex-col rounded-3xl border border-line bg-surface p-6 sm:p-7 shadow-[0_10px_30px_rgba(30,58,43,0.06)] transition-shadow hover:shadow-[0_24px_55px_rgba(30,58,43,0.13)]"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="grid place-items-center w-14 h-14 rounded-2xl bg-badge text-center shrink-0">
                    <span>
                      <span className="block font-serif font-bold text-lg leading-none text-forest">{e.day}</span>
                      <span className="block font-mono text-[8.5px] uppercase tracking-[0.1em] text-soft mt-1">{e.freq}</span>
                    </span>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[9.5px] uppercase tracking-[0.12em] ${cat.chip}`}>
                    <Icon size={11} /> {cat.label}
                  </span>
                </div>
              </div>
              <h3 className="mt-5 font-serif text-xl sm:text-[1.35rem] font-semibold leading-snug">{e.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-soft flex-1">{e.desc}</p>
              <div className="mt-5 pt-4 border-t border-line space-y-2">
                <p className="flex items-center gap-2.5 text-xs text-ink/80">
                  <Clock3 size={13} className="text-terra shrink-0" /> {e.time} WIB
                </p>
                <p className="flex items-center gap-2.5 text-xs text-ink/80">
                  <MapPin size={13} className="text-terra shrink-0" /> {e.place}
                </p>
              </div>
            </motion.article>
          );
        })}
      </div>
    </div>
  </section>
);
